// Façade de la mesure d'audience.
//
// Le reste du site n'importe jamais `posthog-js` : il appelle les huit
// fonctions de `events.js`, qui passent toutes par `track()` ci-dessous. La
// bibliothèque n'est chargée qu'au moment de l'accord — il n'y a pas d'`init()`
// dans `src/index.js`. Le critère « aucune requête vers eu.i.posthog.com sans
// consentement » repose ainsi sur un fait de construction (un module jamais
// importé n'émet rien) plutôt que sur le comportement interne d'une
// initialisation en opt-out, qu'aucune documentation ne garantit et qu'une
// mise à jour pourrait changer. Bénéfice second : qui refuse ne télécharge
// même pas la bibliothèque.
//
// La dépendance ne va que dans un sens. Ce module observe le consentement ;
// `ConsentContext` ignore l'existence de PostHog et n'a pas à être modifié
// pour changer d'outil de mesure.
import { clearAnalyticsCookies } from '../consent/cookies'

const CLE = process.env.REACT_APP_POSTHOG_KEY
const HOTE = process.env.REACT_APP_POSTHOG_HOST

/**
 * Intervalle de vidage de la file d'envoi de `posthog-js`, en millisecondes.
 *
 * Exporté parce qu'une garantie en dépend et qu'un test doit pouvoir la
 * vérifier : un événement capturé avant un retrait part au vidage suivant,
 * donc APRÈS le retrait (voir `disableAnalytics()`). `retrait.test.js` doit
 * attendre au-delà de ce vidage pour mesurer l'état définitif ; il dérive son
 * attente de cette constante plutôt que de la deviner, de sorte que la changer
 * ici déplace la fenêtre du test avec elle.
 */
export const INTERVALLE_DE_VIDAGE_MS = 3000

const OPTIONS = {
  // Épingle le jalon de comportement de la bibliothèque, pour la même raison
  // que `cookie_expiration` plus bas : sans valeur explicite, ce réglage
  // retombe sur son défaut (`'unset'`, les comportements historiques), et une
  // mise à jour de posthog-js pourrait déplacer ce défaut sans que personne
  // ne le remarque ici. '2026-06-25' est la valeur la plus récente que la
  // version installée (1.414.0) reconnaît.
  defaults: '2026-06-25',
  api_host: HOTE,
  // Le site est une application monopage : laissé à son défaut, PostHog
  // n'enregistrerait qu'une seule page pour toute la visite. Les pageviews
  // sont émis à la main par `usePageviews`.
  capture_pageview: false,
  // Rien ne l'apparie à nos pageviews manuels.
  capture_pageleave: false,
  // Activée, l'autocapture enregistre tous les clics et soumissions de
  // formulaire sans instrumentation : au-delà de ce que la description de la
  // finalité annonce au visiteur, et de quoi noyer les huit événements nommés.
  autocapture: false,
  // Aucun profil créé pour les visiteurs anonymes.
  person_profiles: 'identified_only',
  respect_dnt: true,
  // 365 est déjà le défaut de posthog-js. Posé explicitement quand même :
  // c'est la seule ligne de code qui rend vraie ou fausse une phrase publiée
  // — l'article 7 des CGU annonce « 13 mois au maximum » pour `ph_*`. Elle ne
  // doit pas dépendre du défaut d'une dépendance, qu'une mise à jour pourrait
  // changer sans que personne ne relise l'article 7.
  cookie_expiration: 365,
  // 3000 ms est déjà le défaut de posthog-js. Posé explicitement pour
  // exactement la raison de `cookie_expiration` juste au-dessus : une garantie
  // en dépend, et elle ne doit pas reposer sur le défaut d'une dépendance
  // qu'une mise à jour pourrait déplacer en silence. Ici la garantie est celle
  // documentée sur `disableAnalytics()` — le délai au bout duquel plus rien ne
  // part après un retrait. La valeur est bornée à [250, 5000] par la
  // bibliothèque.
  //
  // ATTENTION à l'imbrication : posé à plat, `flush_interval_ms` est
  // silencieusement ignoré — il atterrit dans la configuration comme clé
  // inconnue pendant que la file garde son défaut. Vérifié.
  request_queue_config: { flush_interval_ms: INTERVALLE_DE_VIDAGE_MS },
  // Ces trois options rendent le RETRAIT effectif, et elles seules.
  //
  // Elles ne reviennent pas sur la décision 1, qui refusait de faire reposer
  // « aucune requête avant l'accord » sur un réglage interne de la
  // bibliothèque : cette garantie-là reste portée par l'`import()` dynamique,
  // un module jamais importé n'émettant rien. C'est l'autre moitié du problème
  // qui est traitée ici — ce qui se passe APRÈS un accord retiré.
  //
  // Sans elles, la mesure a montré qu'un retrait laissait revenir
  // `ph_<clé>_posthog` 300 ms plus tard, et que la bibliothèque se croyait de
  // nouveau autorisée à capturer. `reset()` supprime la clé
  // `__ph_opt_in_out_<clé>` (que la purge des traceurs effacerait de toute
  // façon) ; sans valeur stockée, l'état retombe sur son défaut, et le défaut
  // de posthog-js est « autorisé ». En opt-out par défaut, la même absence de
  // valeur signifie « en retrait », qui est la bonne lecture d'un
  // consentement effacé. `charger()` rétablit l'accord une fois le
  // consentement constaté.
  opt_out_capturing_by_default: true,
  // L'écriture de persistance est différée de 250 ms (jalon '2026-06-25') :
  // elle repassait donc APRÈS la purge synchrone et réécrivait le cookie et
  // le stockage local. En opt-out de persistance par défaut, plus rien n'est
  // écrit tant que l'accord n'a pas été rétabli.
  opt_out_persistence_by_default: true,
  // `reset()` appelle `reloadFeatureFlags()` : le retrait lui-même émettait un
  // POST /flags/ portant un identifiant neuf, le fuseau horaire, le jeton de
  // projet — et, au niveau réseau, l'adresse IP du visiteur. Le site n'utilise
  // aucun feature flag ; cette requête n'a donc rien à financer. Sa réponse
  // réenregistrait en outre des propriétés après la purge.
  advanced_disable_flags: true,
  // Hors périmètre de l'issue #77, désactivés explicitement plutôt que
  // laissés à leur défaut.
  disable_session_recording: true,
  disable_surveys: true,
  // Ces quatre collecteurs se décident normalement CÔTÉ SERVEUR, par
  // configuration distante : laissés à `undefined`, ils s'allument sur simple
  // basculement d'un réglage dans le projet PostHog, sans passer par une revue
  // de ce dépôt. Posés à `false`, le réglage distant ne peut plus les
  // rallumer — le verrou existe des deux côtés. `advanced_disable_flags`
  // coupe déjà le canal qui les transporte, mais la documentation de cette
  // option demande explicitement de configurer côté client tout collecteur
  // dont on dépend : le jour où ce drapeau tomberait, ces quatre lignes
  // resteraient.
  capture_heatmaps: false,
  capture_performance: false,
  capture_exceptions: false,
  capture_dead_clicks: false,
}

// L'état vit dans des variables de module : conséquence assumée d'une façade
// plutôt que d'un contexte React. La mesure ne rend rien, aucun composant n'a
// besoin d'être re-rendu parce que PostHog vient de se charger.
let posthog = null
let chargement = null
let actif = false
let echecSignale = false
let dernierChemin = null

function arreterLaCapture() {
  try {
    posthog.opt_out_capturing()
    posthog.reset()
  } catch (error) {
    console.error('Arrêt de la mesure d’audience incomplet', error)
  }
}

async function charger() {
  try {
    const module = await import('posthog-js')
    const instance = module.default ?? module.posthog
    instance.init(CLE, OPTIONS)
    posthog = instance
  } catch (error) {
    // Réseau coupé, bloqueur de publicité, CDN indisponible : la mesure reste
    // inactive et le site fonctionne. Journalisé une seule fois — sans ce
    // drapeau, chaque changement d'avis du visiteur rejouerait le message.
    if (!echecSignale) {
      echecSignale = true
      console.error('Mesure d’audience indisponible', error)
    }
    return
  }

  // Le consentement a pu être retiré pendant le chargement : on n'émet rien,
  // on met la bibliothèque en retrait, et on rejoue la purge. Sans ce dernier
  // appel, la séquence « retrait pendant l'import() » laisse des traceurs :
  // disableAnalytics() purge un stock encore vide (rien n'est encore écrit) ;
  // puis init(), juste au-dessus, écrit ph_<token>_posthog ; puis
  // arreterLaCapture() le réécrit encore via reset(). Le visiteur qui vient
  // de refuser repartirait avec des traceurs posés après son refus — la même
  // panne que celle qui justifie déjà le rappel de clearAnalyticsCookies()
  // dans disableAnalytics(), un cran plus haut.
  if (!actif) {
    arreterLaCapture()
    clearAnalyticsCookies()
    return
  }

  // L'opt-out inscrit dans OPTIONS n'est qu'un état de départ : il donne au
  // consentement effacé le sens « en retrait » plutôt que « autorisé ». Il est
  // levé ici, sinon la mesure n'émettrait jamais rien. L'accord est donné
  // APRÈS le garde ci-dessus, et non juste après `init()` : le visiteur qui a
  // retiré son consentement pendant l'`import()` ne doit pas être autorisé une
  // fraction de seconde, même pour être aussitôt remis en retrait.
  //
  // `captureEventName: null` neutralise l'événement `$opt_in` que la
  // bibliothèque émettrait sinon : il n'est ni le `$pageview` ni l'un des huit
  // événements métier annoncés au visiteur.
  posthog.opt_in_capturing({ captureEventName: null })

  capturePageview(window.location.pathname)
}

/**
 * Active la mesure. Sans clé de projet, ne fait rien — et surtout, ne
 * déclenche aucun `import()`. Renvoie une promesse résolue quand la
 * bibliothèque est prête, ce dont seuls les tests ont besoin.
 */
export function enableAnalytics() {
  if (!CLE) return Promise.resolve()

  actif = true

  if (posthog) {
    // Même neutralisation qu'après `init()` : un visiteur qui redonne son
    // accord ne doit pas produire d'événement que la description de la
    // finalité n'annonce pas.
    posthog.opt_in_capturing({ captureEventName: null })
    return Promise.resolve()
  }

  if (!chargement) chargement = charger()
  return chargement
}

/**
 * Arrête la mesure et efface ses traceurs.
 *
 * `clearAnalyticsCookies()` est rappelée ici bien que `ConsentContext` l'appelle
 * déjà : ce dernier purge de façon synchrone, au moment même du clic, alors que
 * cette fonction est appelée depuis un effet, donc après le rendu. La purge de
 * `ConsentContext` passerait ainsi AVANT l'arrêt de PostHog, qui aurait le temps
 * de réécrire ses clés derrière elle. La fonction est documentée idempotente :
 * le double appel est sans effet.
 *
 * CE QUE CE RETRAIT NE FAIT PAS, sciemment. `posthog-js` groupe ses envois et
 * vide sa file toutes les `INTERVALLE_DE_VIDAGE_MS`. Un événement capturé avant
 * le retrait part donc APRÈS lui. C'est connu et assumé :
 * l'événement a été collecté sous un consentement alors valide, le retrait
 * arrête la collecte future, et aucun traceur ne survit. Vider la file
 * supposerait de manipuler `_requestQueue`, marqué interne — aucune API
 * publique ne le permet, `shutdown()` faisant l'inverse en poussant la file
 * vers le réseau. S'appuyer sur des entrailles qu'une montée de version peut
 * déplacer est précisément ce que la décision 1 du cahier des charges refuse.
 * `retrait.test.js` épingle ce comportement pour qu'il reste celui-là.
 */
export function disableAnalytics() {
  actif = false
  // Un retrait puis un nouvel accord sur la même page doit pouvoir produire un
  // pageview : le chemin mémorisé n'a plus lieu d'être.
  dernierChemin = null

  if (posthog) arreterLaCapture()

  clearAnalyticsCookies()
}

/**
 * Envoie un événement métier. N'est jamais appelée directement par un
 * composant : `events.js` expose les huit fonctions nommées.
 */
export function track(nom, proprietes) {
  if (!actif || !posthog) return
  try {
    posthog.capture(nom, proprietes)
  } catch (error) {
    // Silence volontaire. Ces appels sont posés sur les liens de conversion du
    // site : aucun clic ne doit échouer parce que la mesure a un problème.
  }
}

/**
 * Envoie un `$pageview`, sauf si le chemin est identique au précédent.
 *
 * Au démarrage avec un consentement déjà en mémoire, deux chemins mènent au
 * premier pageview — la fin du chargement ci-dessus, et le montage de
 * `usePageviews`. Le second est aujourd'hui inerte parce que le module n'est
 * pas encore actif, mais le critère « exactement un `$pageview`, jamais deux »
 * ne doit pas dépendre de cet ordonnancement. La déduplication le rend vrai
 * quel que soit l'ordre.
 *
 * Elle ne bloque que des appels CONSÉCUTIFS identiques : un aller-retour
 * `/accueil → /services → /accueil` produit bien trois pageviews.
 */
export function capturePageview(chemin) {
  if (!actif || !posthog) return
  if (chemin === dernierChemin) return
  dernierChemin = chemin
  try {
    posthog.capture('$pageview')
  } catch (error) {
    // Même raison que dans track().
  }
}
